import type { TvRadioCategoryDto, WbsClient } from '@bldsoft/cappie/api/wbs'
import type {
  API,
  CategoryWithChannelsApi,
  EpgInfo,
  EpgInfoDto,
  MediaUrl,
  Page,
  TvCategory,
  TvChannel,
  TvEpgApiDto,
} from '@setplex/tria-api'
import type { AdapterDefaults } from '../../index.h'

import { ensureOk, payload } from '@bldsoft/cappie/rest'
import { CarouselTypes as CarouselTypesEnum } from '@setplex/tria-api'
import * as v from 'valibot'
import { DEFAULT_PAGE_SIZE } from '../../constants/generic'
import { getFirstPagePaginateInfo } from '../../utils/pageInfo'
import { RecentlyWatchedChannelsPageSchema } from './schemas'
import {
  formatChannel,
  formatChannelPlaylist,
  formatChannelUpdatedOnly,
  formatEPGArray,
  formatEPGPrograms,
  getPaginatedTvPlaylist,
} from './tv.format'

const epgToDateStringFormat = (str: number) =>
  new Date(str).toISOString().replace(/\.\d{3}Z$/, 'Z')

export function use(
  client: WbsClient,
  tv: API['tv'],
  _api: API,
  defaults: AdapterDefaults
): void {
  // GET /v3/channels
  tv.getAllTvChannelsByPageFx.use(
    async ({
      page = 0,
      count = defaults.count || 36,
      categoryId,
      'only-favorites': onlyFavorites,
      signal,
      collectionId,
    }) => {
      const channels = await payload(
        client.get.api.v3.channels({
          query: {
            page,
            count,
            categoryId,
            collectionId,
            'only-favorites': onlyFavorites,
            type: 'TV',
          },
          signal,
        })
      )

      return {
        ...channels,
        content: channels.content.map(formatChannel),
      }
    }
  )

  // GET /v3/library/channels/tv
  tv.getPurchasedTvChannelsFx.use(
    async ({
      page = 0,
      count = defaults.count || 36,
      rented = true,
      purchased = true,
      signal,
    }) => {
      const channels = await payload(
        client.get.api.v3.library.channels.tv({
          query: {
            page,
            count,
            rented,
            purchased,
          },
          signal,
        })
      )

      return {
        ...channels,
        content: channels.content.map(formatChannel),
      }
    }
  )

  // GET /v3/channels/tv
  tv.getTvChannelsByIdBaseFx.use(async ({ ids, signal }) => {
    const channels = await payload(
      client.get.api.v3.channels.tv({ query: { ids }, signal })
    )
    return channels.map(formatChannel) as TvChannel[]
  })

  // GET /v3/channels/tv?updatedTimeOnly=true
  tv.getTvChannelsByIdUpdatedOnlyFx.use(async ({ categoryId, signal }) => {
    const channels = await payload(
      client.get.api.v3.channels.tv({
        query: { categoryId, updatedTimeOnly: true },
        signal,
      })
    )
    return channels.map(formatChannelUpdatedOnly)
  })

  // GET /v3/channels/tv/{channelId}/url
  tv.getTvChannelUrlFx.use(
    async ({ channelId, startTime, rewindType, signal }) => {
      const media = await payload(
        client.get.api.v3.channels.tv(channelId).url({
          query: {
            startTime: startTime != null && rewindType ? startTime : undefined,
            ...(rewindType && { [rewindType]: true }),
          },
          signal,
        })
      )

      if (!media.playbackUrl) {
        throw new Error('Empty playbackUrl in getTvChannelUrlFx')
      }

      return media as MediaUrl
    }
  )

  // GET /v3/channels/tv/categories
  tv.getTvCategoriesByPageFx.use(
    async ({ page, count = defaults.count || 36, signal }) => {
      const categories = (await payload(
        client.get.api.v3.channels.tv.categories({
          query: { page, count },
          signal,
        })
      )) as unknown as Page<TvRadioCategoryDto>
      return categories
    }
  )

  // GET /v3/channels/tv?categoryId=categoryId
  tv.getChannelsByCategoryIdFx.use(
    async ({ page, count = defaults.count || 36, categoryId, signal }) => {
      const channels = await payload(
        client.get.api.v3.channels({
          query: {
            type: 'TV',
            page,
            count,
            categoryId: categoryId || undefined,
          },
          signal,
        })
      )

      return {
        ...channels,
        content: channels.content.map(formatChannel),
      }
    }
  )

  // PATCH /v3/channels/tv/{channelId}
  tv.updateTvChannelFx.use(async ({ id, favorite, signal }) => {
    const body: { id?: number; favorite?: boolean } = {}
    if (id != null) body.id = Number(id)
    if (favorite != null) body.favorite = Boolean(favorite)
    await ensureOk(client.patch.api.v3.channels.tv(id, { body, signal }))
  })

  tv.generateTvPlaylistFx.use(async (params) => {
    const {
      categoryId,
      featured,
      favorite,
      purchased,
      recentlyWatched,
      recommended,
      collectionId,
      signal,
    } = params || {}

    if (recentlyWatched) {
      return {
        categoryId: null,
        channels: [],
        pageInfo: null,
      }
    }

    if (recommended) {
      const channels = await _api.tv.getRealRecommendedTvChannelsFx({ signal })
      return {
        categoryId: null,
        channels: channels.map(formatChannelPlaylist),
        pageInfo: null,
      }
    }

    if (purchased) {
      return getPaginatedTvPlaylist({
        getTvChannelsFX: _api.tv.getPurchasedTvChannelsGridFx,
        signal,
      })
    }

    if (favorite) {
      return getPaginatedTvPlaylist({
        getTvChannelsFX: _api.tv.getOnlyFavoritesTvChannelsGridFx,
        signal,
      })
    }

    if (featured) {
      const featuredChannels = await _api.tv.getFeaturedChannelsFx({ signal })
      return {
        categoryId: 0,
        channels: featuredChannels.map(formatChannelPlaylist),
        pageInfo: getFirstPagePaginateInfo({
          totalElements: featuredChannels.length,
        }),
      }
    }

    return getPaginatedTvPlaylist({
      getTvChannelsFX: _api.tv.getAllTvChannelsByPageFx,
      categoryId,
      collectionId,
      signal,
    })
  })

  const getDefaultAllCategory = (): TvCategory => ({
    id: 0,
    name: 'All Live Channels',
    sortOrder: 0,
    parentCategoryId: 0,
    channelIds: [],
  })
  tv.getTvCategoryFx.use(async ({ categoryId, signal }) => {
    if (categoryId === 0) return getDefaultAllCategory()

    let foundCategory: TvCategory | undefined
    const firstPageCategories = await tv.getTvCategoriesByPageFx({
      page: 0,
      signal,
    })
    foundCategory = firstPageCategories.content.find(
      (item) => item.id === categoryId
    )

    if (foundCategory) {
      return foundCategory
    }

    let pageIndex: number = 0
    while (pageIndex <= (firstPageCategories.totalPages || 0) - 1) {
      pageIndex++
      const pageCategories = await tv.getTvCategoriesByPageFx({
        page: pageIndex,
        signal,
      })
      foundCategory = pageCategories.content.find(
        (item) => item.id === categoryId
      )

      if (foundCategory) {
        return foundCategory
      }
    }

    return foundCategory || null
  })

  tv.initChannelsCategoryFx.use(async ({ categoryId, signal }) => {
    const category = await tv.loadTvCategoryFx({
      categoryId,
    })
    const channels = await tv.loadChannelsFx({ categoryId, page: 0, signal })

    return {
      ...category,
      content: [...channels.content],
    }
  })

  // GET /v3/channels/tv?only-recommended=true
  tv.getRealRecommendedTvChannelsFx.use(
    async ({ count = defaults.count || DEFAULT_PAGE_SIZE, signal }) => {
      const channels = await payload(
        client.get.api.v3.channels.tv({
          query: { 'only-recommended': true, count },
          signal,
        })
      )

      return (channels || []).map(formatChannel)
    }
  )

  // POST /media/tv/epg
  tv.getEpgInformationFormattedFx.use(
    async ({ epgIds, channels, period, signal }) => {
      const body: {
        channelEpgIds: string[]
        fromDate: string
        toDate: string
      } = {
        channelEpgIds: epgIds,
        // format to string BE could handle
        fromDate: new Date(period.start)
          .toISOString()
          .replace(/\.\d{3}Z$/, 'Z'),
        toDate: epgToDateStringFormat(period.end),
      }

      if (!epgIds?.length) return { epg: {}, period } // no sense request // b.e. returns empty obj in that case

      const epg = (await payload(
        client.post.api.media.tv.epg({ body, signal })
      )) as TvEpgApiDto

      return {
        epg: formatEPGPrograms(epg, channels, period),
        period,
      }
    }
  )

  // POST /media/tv/epg
  tv.getEpgInformationFx.use(async ({ epgIds, period, signal }) => {
    const body: { channelEpgIds: string[]; fromDate: string; toDate: string } =
      {
        channelEpgIds: epgIds,
        fromDate: new Date(period.start)
          .toISOString()
          .replace(/\.\d{3}Z$/, 'Z'),
        toDate: epgToDateStringFormat(period.end),
      }

    if (!epgIds?.length) return { epg: {}, period } // no sense request // b.e. returns empty obj in that case

    const epg = (await payload(
      client.post.api.media.tv.epg({ body, signal })
    )) as TvEpgApiDto

    return {
      epg,
      period,
    }
  })

  // GET /media/tv/epg
  tv.getProgramEPGInfoFx.use(async ({ epgId, programTitle, start, signal }) => {
    // request accepts only start coz Get /media/tv/epg/ accepts only start and its period - 7 days
    if (!epgId) return [] as EpgInfo[] // no sense request // b.e. returns empty array in that case

    const startFormatted = new Date(start)
      .toISOString()
      .replace(/\.\d{3}Z$/, 'Z')

    // this route spells out `web` in the OpenAPI document, so it has no `{platform}` bind
    const programmes = (await payload(
      client.get.web.api.media.tv.epg(epgId, {
        query: { title: programTitle, start: startFormatted },
        signal,
      })
    )) as EpgInfoDto[]

    return formatEPGArray(epgId, programmes)
  })

  tv.getCategoriesWithChannelsFx.use(async (args) => {
    const { page, count, signal } = args
    const pageOfCategories = await tv.getTvCategoriesByPageFx({
      page,
      count,
      signal,
    })
    const categoriesIdsArray = pageOfCategories.content.map(
      (category) => category.id
    )

    const channelsPromises = categoriesIdsArray.map((categoryId) =>
      tv
        .getChannelsByCategoryIdFx({
          page: 0,
          categoryId: Number(categoryId),
          signal,
        })
        .then((data) => ({
          categoryId: Number(categoryId),
          data,
        }))
        .catch(() => ({
          categoryId: Number(categoryId),
          data: undefined,
        }))
    )

    const channelsPages = await Promise.all(channelsPromises)

    const formattedCategory = pageOfCategories.content.map(
      (category): CategoryWithChannelsApi => {
        const channelsPageOfCategory = channelsPages.find(
          (channels) => channels.categoryId === category.id
        )?.data

        const isMoreThanOnePage =
          !channelsPageOfCategory?.last ||
          channelsPageOfCategory?.content.length > DEFAULT_PAGE_SIZE

        const isChannelExist = channelsPageOfCategory?.content.length
        const pageOfChannels: Page<TvChannel> = isChannelExist
          ? {
              ...channelsPageOfCategory,
              content: channelsPageOfCategory.content,
            }
          : {
              content: [],
              ...getFirstPagePaginateInfo({
                totalElements: 0,
              }),
            }

        return {
          ...category,
          content: pageOfChannels,
          isMoreThanOnePage,
        }
      }
    )

    return {
      ...pageOfCategories,
      content: formattedCategory,
    }
  })

  tv.getFeaturedChannelsFx.use(async (args) => {
    const { signal } = args || {}

    const featured = await _api.banners.getFeaturedCarouselFx({ signal })

    const featuredTvCarousel = featured.find(
      (carousel) => carousel.type === CarouselTypesEnum.TV_CHANNEL
    )?.content

    return featuredTvCarousel?.map(({ channel }) => channel as TvChannel) ?? []
  })

  // GET /v3/channels/recently-watched
  tv.getRecentlyWatchedChannelsFx.use(
    async (
      args:
        | {
            'page'?: number
            'count'?: number
            'sort-by'?: string
            'sort-order'?: string
            'signal'?: AbortSignal
          }
        | undefined
        | void
    ) => {
      const { signal } = args ?? {}

      const json = await payload(
        client.get.api.v3.channels.recentlyWatched({
          query: {
            page: args?.page,
            count: args?.count,
            'sort-by': args?.['sort-by'],
            'sort-order': args?.['sort-order'],
          },
          signal,
        })
      )
      const page = v.parse(RecentlyWatchedChannelsPageSchema, json)

      // in the future this should be changed to simple `return page`
      // but as for now, we need to format the response using current formatting function,
      // this is like intermediate step for transition to new formatting via schemas
      return {
        ...page,
        content: page.content.map(formatChannel),
      }
    }
  )

  // PUT /v3/channels/recently-watched/{channelId}
  tv.addRecentlyWatchedChannelFx.use(async ({ id }: { id: number }) => {
    await ensureOk(client.put.api.v3.channels.recentlyWatched(id))
  })
}
